import {
  Box, Paper, Table, TableBody, TableCell,
  TableHead, TablePagination, TableRow, Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TargetsApi from 'api/executive/targets.api'
import type { TargetAuditLog } from 'api/executive/executive.types'
import TabLoading from './TabLoading'

const PAGE_SIZE = 50

export default function HistoryTab() {
  const { t } = useTranslation()
  const [items, setItems] = useState<TargetAuditLog[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    TargetsApi.getAuditLog({ limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((res) => {
        setItems(res.items)
        setTotal(res.total)
      })
      .finally(() => setLoading(false))
  }, [page])

  // Resource label = the History column's primary identifier. Show the
  // tab name verbatim — the same wording used in the tab strip — so a
  // glance at the table tells you which screen the change came from.
  const tabName = (resourceType: TargetAuditLog['resourceType']): string => {
    switch (resourceType) {
      case 'revenue_target':         return t('DASHBOARD_TARGETS.TAB.REVENUE')
      case 'procedure_target':       return t('DASHBOARD_TARGETS.TAB.PROCEDURE')
      case 'age_groups':             return t('DASHBOARD_TARGETS.TAB.AGE_GROUPS', 'กลุ่มอายุ')
      case 'occupation_groups':      return t('DASHBOARD_TARGETS.TAB.OCCUPATIONS', 'อาชีพ')
      case 'promotion_groups':       return t('DASHBOARD_TARGETS.TAB.PROMOTIONS', 'โปรโมชั่น')
      case 'procedure_type_filter':  return t('DASHBOARD_TARGETS.TAB.PROCEDURE_TYPES', 'ประเภทหัตถการ')
      default:                       return resourceType
    }
  }

  // Human-readable name for the per-field key that came back from the
  // audit log. Falls through to the raw key for anything we don't know
  // about yet so future fields are still visible (just untranslated).
  const fieldLabel = (field: string): string => {
    switch (field) {
      case 'buckets':       return 'จำนวนกลุ่ม'
      case 'items':         return 'จำนวนรายการ'
      case 'added':         return 'เพิ่ม'
      case 'removed':       return 'ลบ'
      case 'groupAdded':    return 'สร้างกลุ่ม'
      case 'groupRemoved':  return 'ลบกลุ่ม'
      case 'itemAdded':     return 'เพิ่มในกลุ่ม'
      case 'itemRemoved':   return 'ลบจากกลุ่ม'
      case 'rangeChanged':  return 'แก้ไขช่วงอายุ'
      case 'caseCount':     return 'จำนวนเคส'
      case 'amount':        return 'ยอด'
      case 'targetValue':   return 'เป้าหมาย'
      default:              return field
    }
  }

  // Render a raw audit value without leaking "[object Object]" / "null"
  // into the history string.
  const formatValue = (v: unknown): string => {
    if (v == null) return '—'
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v)
    try { return JSON.stringify(v) } catch { return '—' }
  }

  // Fields that carry a value on only one side (action-style, not
  // before/after). For these we collapse "X → Y" to "เพิ่ม: X" /
  // "ลบกลุ่ม: Y" / ... instead of forcing the arrow form.
  const ONE_SIDED = new Set(['added', 'removed', 'groupAdded', 'groupRemoved', 'itemAdded', 'itemRemoved'])

  const renderFieldChange = (fc: TargetAuditLog['fieldChanges'][number]): string => {
    // `target` is a context identifier prepended to procedure/revenue
    // target rows ("ขูดหินปูน · สาขา A · 1/2569"). Render the value
    // directly so it reads as a heading, not "target: ...".
    if (fc.field === 'target') {
      const value = fc.newValue ?? fc.oldValue
      return formatValue(value)
    }
    if (ONE_SIDED.has(fc.field)) {
      const value = fc.newValue ?? fc.oldValue
      return `${fieldLabel(fc.field)}: ${formatValue(value)}`
    }
    return `${fieldLabel(fc.field)}: ${formatValue(fc.oldValue)} → ${formatValue(fc.newValue)}`
  }

  // Batch submits (procedure_target) emit multiple `target` headers in
  // a single audit row — chunk fieldChanges by target so each affected
  // item gets its own line and the row stays scannable.
  const groupByTarget = (
    changes: TargetAuditLog['fieldChanges']
  ): TargetAuditLog['fieldChanges'][] => {
    const groups: TargetAuditLog['fieldChanges'][] = []
    let current: TargetAuditLog['fieldChanges'] = []
    for (const fc of changes) {
      if (fc.field === 'target') {
        if (current.length) groups.push(current)
        current = [fc]
      } else {
        current.push(fc)
      }
    }
    if (current.length) groups.push(current)
    return groups
  }

  const describeChange = (log: TargetAuditLog): string => {
    const resource = tabName(log.resourceType)
    if (log.action === 'delete') return `ลบ ${resource}`
    if (!log.fieldChanges?.length) {
      return log.action === 'create' ? `สร้าง ${resource}` : `แก้ไข ${resource}`
    }
    const groups = groupByTarget(log.fieldChanges)
    const prefix = log.action === 'create' ? `สร้าง ${resource}` : resource
    // Single-target (or non-batch) row → keep on one line; batched rows
    // → one target per line so each item stays readable.
    const sep = groups.length > 1 ? '\n' : ' · '
    const body = groups.map((g) => g.map(renderFieldChange).join(' · ')).join(sep)
    return `${prefix} — ${body}`
  }

  if (loading) {
    return <TabLoading />
  }

  if (items.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        {t('EXECUTIVE.EMPTY')}
      </Typography>
    )
  }

  return (
    <Paper>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('DASHBOARD_TARGETS.HISTORY_TABLE.DATE')}</TableCell>
            <TableCell>{t('DASHBOARD_TARGETS.HISTORY_TABLE.USER')}</TableCell>
            <TableCell>{t('DASHBOARD_TARGETS.HISTORY_TABLE.CHANGE')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id}>
              <TableCell>{new Date(it.timestamp).toLocaleString('th-TH')}</TableCell>
              <TableCell>{it.userName}</TableCell>
              <TableCell sx={{ whiteSpace: 'pre-line' }}>{describeChange(it)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
      />
    </Paper>
  )
}
